import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Sparkles, HeartPulse, BookOpen, Palette, Cpu, Compass, GraduationCap, Mail, Mic, Users } from "lucide-react";
import { ListenButton } from "./ListenButton";
import { FOUNDATIONS } from "../config/foundations";

const FOUNDATION_ICONS = [Sparkles, HeartPulse, BookOpen, Palette, Cpu, Compass];
const INITIATIVE_ICONS = [BookOpen, Mail, GraduationCap, BookOpen, Mic, Users];

export default function World() {
  const { t } = useTranslation();
  const foundations = t("world.foundations", { returnObjects: true }) || [];
  const initiatives = t("world.initiatives", { returnObjects: true }) || [];

  return (
    <div className="py-12 space-y-24 md:space-y-32">

      <section className="max-w-4xl mx-auto px-6 text-center space-y-6">
        <span className="text-[10px] font-sans tracking-[0.3em] uppercase text-[var(--accent-bronze)] font-semibold block">{t("world.eyebrow")}</span>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif text-primary-custom tracking-tight leading-tight">{t("world.heading")}</h1>
        <p className="font-serif text-base sm:text-lg text-secondary-custom max-w-2xl mx-auto leading-relaxed font-light">{t("world.subtitle")}</p>
        <div className="flex justify-center pt-2">
          <ListenButton text={[t("world.heading"), t("world.subtitle"), ...((t("world.foundations", { returnObjects: true }) || []).map((f) => `${f.title}. ${f.desc}`))].filter(Boolean).join(" ")} testId="world-page-listen" idleKey="listen.toPage" />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12">
        <figure className="space-y-5">
          <div className="aspect-[4/5] sm:aspect-[16/10] overflow-hidden border border-subtle-custom">
            <img src="/images/world-hero.jpg" alt={t("world.captionTitle")} loading="lazy" className="w-full h-full object-cover" />
          </div>
          <figcaption className="space-y-1">
            <span className="text-[10px] font-sans tracking-[0.25em] uppercase text-[var(--accent-bronze)] font-semibold block">{t("world.captionEyebrow")}</span>
            <h3 className="font-serif text-xl md:text-2xl text-primary-custom font-medium">{t("world.captionTitle")}</h3>
          </figcaption>
        </figure>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 space-y-12">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-serif tracking-tight text-primary-custom">{t("world.foundationsHeading")}</h2>
          <p className="font-serif text-xs italic text-secondary-custom mt-2">{t("world.foundationsSubtitle")}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {foundations.map((node, idx) => {
            const Icon = FOUNDATION_ICONS[idx % FOUNDATION_ICONS.length];
            const slug = FOUNDATIONS[idx] ? FOUNDATIONS[idx].slug : "";
            return (
              <Link key={idx} to={`/foundations/${slug}`} data-testid={`world-foundation-link-${idx}`} className="group border border-subtle-custom bg-surface-custom/40 p-10 rounded-none hover:border-[var(--accent-bronze)] transition-all duration-500 flex flex-col justify-between">
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div className="w-10 h-10 border border-subtle-custom bg-primary-custom flex items-center justify-center">
                      <Icon className="w-4 h-4 text-[var(--accent-bronze)]" strokeWidth={1} />
                    </div>
                    <span className="text-[9px] font-sans tracking-[0.15em] text-secondary-custom uppercase bg-primary-custom px-2 py-0.5 border border-subtle-custom">{t("world.foundationLabel")} {String(idx + 1).padStart(2, '0')}</span>
                  </div>
                  <h3 className="text-lg font-serif font-medium text-primary-custom group-hover:text-[var(--accent-bronze)] transition-colors">{node.title}</h3>
                  <p className="font-sans text-xs sm:text-sm text-secondary-custom leading-relaxed">{node.desc}</p>
                </div>
                <div className="pt-8 flex items-center gap-2 text-[10px] font-sans tracking-[0.15em] uppercase text-secondary-custom group-hover:text-[var(--accent-bronze)] transition-colors">
                  <span>{t("home.foundationsExplore")}</span>
                  <ArrowRight className="w-3 h-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="bg-surface-custom py-24 transition-colors duration-500 border-y border-subtle-custom">
        <div className="max-w-7xl mx-auto px-6 md:px-12 space-y-12">
          <div className="max-w-3xl space-y-4">
            <span className="text-[10px] font-sans tracking-[0.25em] uppercase text-[var(--accent-bronze)] font-semibold block">{t("world.buildingEyebrow")}</span>
            <h2 className="text-3xl font-serif tracking-tight text-primary-custom">{t("world.buildingHeading")}</h2>
            <p className="font-serif text-sm sm:text-base text-secondary-custom leading-relaxed font-light">{t("world.buildingIntro")}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {initiatives.map((item, idx) => {
              const Icon = INITIATIVE_ICONS[idx % INITIATIVE_ICONS.length];
              return (
                <div key={idx} className="border border-subtle-custom bg-primary-custom p-8 rounded-none hover:border-[var(--accent-bronze)] transition-all duration-500 flex flex-col justify-between">
                  <div className="space-y-5">
                    <div className="flex items-center justify-between">
                      <div className="w-9 h-9 border border-subtle-custom bg-surface-custom flex items-center justify-center">
                        <Icon className="w-4 h-4 text-[var(--accent-bronze)]" strokeWidth={1} />
                      </div>
                      <span className="text-[9px] font-sans tracking-[0.15em] text-secondary-custom uppercase bg-surface-custom px-2 py-0.5 border border-subtle-custom">{item.status}</span>
                    </div>
                    <h4 className="text-lg font-serif font-medium text-primary-custom">{item.title}</h4>
                    <p className="font-sans text-xs text-secondary-custom leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 text-center space-y-8">
        <span className="text-[10px] font-sans tracking-[0.3em] uppercase text-[var(--accent-bronze)] font-semibold block">{t("world.pathwayEyebrow")}</span>
        <h2 className="text-2xl sm:text-3xl font-serif text-primary-custom tracking-tight">{t("world.pathwayHeading")}</h2>
        <p className="font-serif text-sm sm:text-base text-secondary-custom leading-relaxed font-light">{t("world.pathwayBody")}</p>
        <div className="pt-4">
          <Link to="/contact" data-testid="world-cooperation-cta" className="inline-flex items-center space-x-2 border border-primary px-8 py-3.5 text-xs uppercase tracking-[0.2em] font-medium hover:bg-[var(--accent-bronze)] hover:text-white hover:border-[var(--accent-bronze)] transition-all duration-300">
            <span>{t("world.getInTouch")}</span>
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

    </div>
  );
}
